import { BodyM, Caption, Description } from '@casavo/base-ui'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import React, { memo } from 'react'

import PropertyIcon from '@/components/PropertyIcon'
import { Translated } from '@/components/Translated'
import { useAuthentication } from '@/context/AuthProvider'
import { useDevice } from '@/context/Device'
import { Badge } from '@/design-lib/components/Badge'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { absoluteCenterPositioning, ellipsis, paddingBottom } from '@/shared-styles/styles.css'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import { getPriceStatus, roundPrice } from '@/utils/utils-potential-properties'

import { cardBody, cardFooter, cardImage, cardImageWrapper, cardWrapper, unlockPropertyMessage } from './styles.css'

export type PriorityInfo = {
  heading?: string
  icon?: 'rooms' | 'bedrooms' | 'bathroom'
  text?: string | number
}

export type Props = {
  badge?: {
    left?: React.ReactNode
  }
  details: React.ReactNode[]
  hasOutdoorSpace?: boolean
  headline?: React.ReactElement
  image: {
    alt: string
    src: string
  }
  info: PriorityInfo[]
  isHighEfficiency?: boolean
  labels?: {
    highEfficienyLabel?: string
    outdoorSpaceLabel?: string
  }
  price: { askedPrice: { cents: number }; estimatedListingPrice: { cents: number } }
  status: string
  title: string
}

export const CardPriority: React.FC<Props> = memo((props: Props) => {
  const { lang, t } = useTranslation()
  const formattedPrice = formatListingSellingPrice(false, roundPrice(props.price.askedPrice), t, lang)
  const isGoodPrice =
    props.price.estimatedListingPrice &&
    getPriceStatus(props.price.askedPrice?.cents, props.price.estimatedListingPrice.cents).isGoodPrice
  const showPriceStatusBadge = props.status === 'priority' && isGoodPrice
  const device = useDevice()
  const imageHeight = device === 'desktop' ? 200 : 160
  const { isLoading: isUserLoading, user } = useAuthentication()
  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  const blurredImage = !user && !isUserLoading ? '&blur=100' : ''

  const baseSrc = `${props.image.src}?auto=compress,format&h=${imageHeight}${blurredImage}`
  const srcSet = `
  ${baseSrc}&dpr=1 1x,
  ${baseSrc}&dpr=2 2x,
  ${baseSrc}&dpr=3 3x
`

  return (
    <div className={cardWrapper}>
      <div className={cardImageWrapper}>
        {props.badge && props.badge.left}
        {!user && !isUserLoading && (
          <div className={cn(unlockPropertyMessage, absoluteCenterPositioning)}>
            <img alt="" src={`${imageBasePath}/images/priorities/priority-card-lock.svg`} />
            <Translated i18Key={'priority:PriorityCard_CTA'} />
          </div>
        )}
        <img
          alt={props.image.alt}
          className={cardImage}
          data-testid="card-main-img"
          height={imageHeight}
          src={`${props.image.src}?auto=compress,format&h=${imageHeight}${blurredImage}`}
          srcSet={srcSet}
          style={{ height: imageHeight }}
          width="100%"
        />
      </div>
      <div className={cardBody}>
        {props.headline && (
          <Caption className={cn(paddingBottom.xs, ellipsis)} color="greyscale500">
            {props.headline}
          </Caption>
        )}

        <Description strong as="h2">
          {props.title}
        </Description>

        <div className={sprinkles({ marginBottom: 'm', marginTop: 'auto' })}>
          {props.details.length > 0 && (
            <div className={sprinkles({ display: 'flex', gap: 's' })}>
              {props.details.map((detail, index) => (
                <div key={index}>{detail}</div>
              ))}
            </div>
          )}

          {props.info.length > 0 && (
            <div
              className={sprinkles({ display: 'flex', gap: 'm', marginTop: 'auto' })}
              data-testid="priority-card-info"
            >
              {props.info.map((info, index) => (
                <div key={index}>
                  <Caption color="greyscale500">{info.heading}</Caption>
                  <div className={sprinkles({ alignItems: 'center', display: 'flex', gap: 'xs' })}>
                    <Caption>{info.text}</Caption>
                    {info.icon && <PropertyIcon kind={info.icon} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {(props?.hasOutdoorSpace || props?.isHighEfficiency) && (
          <div className={sprinkles({ paddingBottom: 's' })}>
            {props?.hasOutdoorSpace && <Badge icon="Tree" styleVariant="greyscale" />}
            {props?.isHighEfficiency && <Badge icon="Sun" styleVariant="greyscale" />}
          </div>
        )}
        <div className={cardFooter}>
          <BodyM data-testid="priority-card-price">{formattedPrice}</BodyM>

          {showPriceStatusBadge && (
            <Badge
              className={sprinkles({
                marginLeft: 'auto',
              })}
              icon="Verified"
              label={t('priority:PriceAnalysis_GoodPrice_Tag')}
              solid={false}
              styleVariant="success"
            />
          )}
        </div>
      </div>
    </div>
  )
})
